import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { CircleX, Plus } from "lucide-react";
import React, { useState } from "react";
import useLocales from "src/hooks/useLocales";
import { useIsInsideTableFilterWrapper } from "src/components/filter-table-wrapper/TableFilterWrapperContext";
import {
  filterChipSx,
  getFilterDialogChipsRowSx,
  filterDialogClearButtonSx,
  filterDialogContentSx,
  filterDialogFooterDividerSx,
  filterDialogFooterSx,
  filterDialogHeaderSx,
  filterDialogRootSx,
  filterDialogTriggerButtonSx,
  filterDialogTriggerRowSx,
  getFilterDialogApplyButtonSx,
  getFilterDialogColors,
} from "src/utils/filterDialogStyles";
import { IFilterDialogProps } from "src/@types/FilterDialogInterfaces";

export type { IAppliedFilter, IFilterDialogStyles } from "src/@types/FilterDialogInterfaces";

const FilterDialog: React.FC<IFilterDialogProps> = ({
  buttonLabel,
  dialogTitle,
  dialogSubtitle,
  appliedFilters,
  isLoading,
  onDelete,
  onClearAll,
  onSubmit,
  children,
  rightSideContent,
  footerLeft,
  clearLabel,
  applyLabel,
  maxWidth = "sm",
  sx = {},
  open: controlledOpen,
  onOpen,
  onClose,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const theme = useTheme();
  const { translate } = useLocales();
  const isInsideTableFilterWrapper = useIsInsideTableFilterWrapper();
  const resolvedClearLabel = clearLabel ?? translate("clear_all");
  const resolvedApplyLabel = applyLabel ?? translate("apply");

  const open = controlledOpen ?? internalOpen;
  const openDialog = (event: React.MouseEvent<HTMLElement>) => {
    if (onOpen) {
      onOpen(event);
    } else {
      setInternalOpen(true);
    }
  };
  const closeDialog = () => {
    if (onClose) {
      onClose();
    } else {
      setInternalOpen(false);
    }
  };

  const { headingColor, labelColor, dostStone, dostDark } = getFilterDialogColors(theme);

  const hasFilters = Object.keys(appliedFilters).length > 0;
  const hasFooter = Boolean(onSubmit || onClearAll);
  const showTriggerDivider = isInsideTableFilterWrapper && hasFilters;

  const handleApply = () => {
    onSubmit?.();
    if (!onClose) {
      setInternalOpen(false);
    }
  };

  return (
    <Box sx={[filterDialogRootSx, sx.root ?? {}] as SxProps<Theme>}>
      <Box
        sx={[
          filterDialogTriggerRowSx,
          showTriggerDivider
            ? { borderBottom: `1px solid ${dostStone}`, mx: -2, px: 2, pb: 1.5 }
            : {},
          sx.triggerRow ?? {},
        ] as SxProps<Theme>}
      >
        <Button
          variant="outlined"
          size="small"
          endIcon={<Plus size={18} />}
          onClick={openDialog}
          disabled={isLoading}
          sx={filterDialogTriggerButtonSx}
        >
          {buttonLabel}
        </Button>

        {rightSideContent && <Box>{rightSideContent}</Box>}
      </Box>

      {hasFilters && (
        <Box
          sx={[
            getFilterDialogChipsRowSx(dostStone),
            sx.chipsRow ?? {},
          ] as SxProps<Theme>}
        >
          {Object.entries(appliedFilters).map(([key, item]) => (
            <Chip
              key={key}
              label={
                item.label
                  ? `${item.label}: ${item.displayValue}`
                  : item.displayValue
              }
              onDelete={onDelete ? () => onDelete(key) : undefined}
              deleteIcon={onDelete ? <CloseIcon /> : undefined}
              size="small"
              sx={[filterChipSx, sx.chip ?? {}] as SxProps<Theme>}
            />
          ))}
        </Box>
      )}

      <Dialog
        open={open}
        onClose={closeDialog}
        maxWidth={maxWidth}
        PaperProps={{ sx: [{ width: "100%" }, sx.dialog ?? {}] as SxProps<Theme> }}
      >
        <Box sx={[filterDialogHeaderSx, sx.dialogHeader ?? {}] as SxProps<Theme>}>
          <Box>
            {dialogTitle && (
              <Typography variant="h6" fontWeight={700} sx={{ color: headingColor }}>
                {dialogTitle}
              </Typography>
            )}
            {dialogSubtitle && (
              <Typography variant="body2" sx={{ color: labelColor, mt: 0.25 }}>
                {dialogSubtitle}
              </Typography>
            )}
          </Box>
          <IconButton onClick={closeDialog}>
            <CircleX strokeWidth="1.5px" size="24px" />
          </IconButton>
        </Box>

        <Divider />

        <DialogContent sx={[filterDialogContentSx, sx.dialogContent ?? {}] as SxProps<Theme>}>
          {children}

          {hasFooter && (
            <>
              <Divider sx={filterDialogFooterDividerSx as SxProps<Theme>} />

              <Box sx={[filterDialogFooterSx, sx.dialogFooter ?? {}] as SxProps<Theme>}>
                <Box>{footerLeft}</Box>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  {onClearAll && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={onClearAll}
                      sx={[
                        filterDialogClearButtonSx,
                        sx.clearButton ?? {},
                      ] as SxProps<Theme>}
                    >
                      {resolvedClearLabel}
                    </Button>
                  )}
                  {onSubmit && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleApply}
                      sx={[
                        getFilterDialogApplyButtonSx(dostDark),
                        sx.applyButton ?? {},
                      ] as SxProps<Theme>}
                    >
                      {resolvedApplyLabel}
                    </Button>
                  )}
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FilterDialog;
